import { useMemo } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { type SelectOption } from '@components/select';
import { showSuccess } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';

import { useCurrentUser, useInvalidateAuthentication } from 'api/authentication/hooks';
import { StepKey } from 'api/authentication/requests';
import { handleError } from 'api/query-client';

import {
  addAzureADSettings,
  checkEmailAddressUsage,
  completeOnboarding,
  completeOnboardingStep,
  completePreboarding,
  createDnsDomain,
  createEmailAddress,
  createSmtpDomain,
  deleteEmailAddress,
  deleteDnsDomain,
  deleteSmtpDomain,
  editBillingDetails,
  editCompanyDetails,
  loadAzureADSettings,
  loadAzureADSettingsForEmail,
  loadBillingDetails,
  loadChargebeePaymentMethodPage,
  loadChargebeePlanPages,
  loadCompanyDetails,
  loadCompanyFormatPresets,
  loadEmailDomains,
  loadEmailSettings,
  loadInvoicePdf,
  loadInvoices,
  loadTimezones,
  refreshSmtpDomainToken,
  removeAzureADSettings,
  sendEmailAddressVerification,
  setDefaultEmailAddress,
  skipOnboarding,
  updateEmailAddress,
  updateSmtpDomain,
  verifyDnsDomain,
  verifyEmailAddress,
  verifyEmailSettings,
} from './requests';

const COMPANY_FORMAT_PRESETS_KEY = ['company-format-presets'];

export const useCompanyDetails = () => {
  return useQuery({
    queryKey: ['company-details'],
    queryFn: () => loadCompanyDetails(),
  });
};

export const useCompanyFormatPresets = () => {
  return useQuery({
    queryKey: COMPANY_FORMAT_PRESETS_KEY,
    queryFn: () => loadCompanyFormatPresets(),
  });
};

export const useInvalidateCompanyFormatPresets = () => {
  const queryClient = useQueryClient();
  const invalidateCompanyFormatPresets = () =>
    queryClient.invalidateQueries(COMPANY_FORMAT_PRESETS_KEY);
  return { invalidateCompanyFormatPresets };
};

type EditCompanyDetailsProps = {
  onSuccessText?: string;
  onSuccess?: () => void;
  shouldNotShowSuccess?: boolean;
};

export const useEditCompanyDetails = (props: EditCompanyDetailsProps = {}) => {
  const { onSuccessText, onSuccess, shouldNotShowSuccess } = props;
  const { t } = useScopedTranslate(locale);

  const queryClient = useQueryClient();
  const { invalidateCurrentUser } = useInvalidateAuthentication();
  const { invalidateCompanyFormatPresets } = useInvalidateCompanyFormatPresets();
  return useMutation({
    mutationFn: editCompanyDetails,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries(['company-details']),
        invalidateCompanyFormatPresets(),
        invalidateCurrentUser(),
      ]);
      !shouldNotShowSuccess && showSuccess(onSuccessText ?? t('companyUpdated'));
      setTimeout(() => {
        onSuccess?.();
      });
    },
    onError: () => {
      handleError(t('companyNotUpdated'));
    },
  });
};

export const useCompletePreboarding = () => {
  const queryClient = useQueryClient();
  const { invalidateCurrentUser } = useInvalidateAuthentication();
  return useMutation({
    mutationFn: completePreboarding,
    onSuccess: async () => {
      await queryClient.invalidateQueries(['company-details']);
      invalidateCurrentUser();
    },
  });
};

export const useSkipOnboarding = () => {
  const { invalidateCurrentUser } = useInvalidateAuthentication();
  return useMutation({
    mutationFn: skipOnboarding,
    onSuccess: invalidateCurrentUser,
  });
};

export const useCompleteOnboarding = () => {
  const { invalidateCurrentUser } = useInvalidateAuthentication();
  return useMutation({
    mutationFn: completeOnboarding,
    onSuccess: invalidateCurrentUser,
  });
};

export const useCompleteOnboardingStep = () => {
  const { invalidateCurrentUser } = useInvalidateAuthentication();
  const currentUser = useCurrentUser();
  const mutation = useMutation({
    mutationFn: completeOnboardingStep,
    onSuccess: () => {
      invalidateCurrentUser();
    },
  });
  return (step: StepKey, travellerJourneyShortId?: string) => {
    const onboardingStatus = currentUser?.company.onboarding?.status;
    const isComplete = onboardingStatus?.steps[step];
    const isCorrectTravellerJourney = travellerJourneyShortId
      ? onboardingStatus?.config.travellerJourneyId === travellerJourneyShortId
      : true;
    if (!isComplete && isCorrectTravellerJourney) {
      mutation.mutate(step);
    }
  };
};

export const useCompanyTimezones = () => {
  return useQuery({
    queryKey: ['company-default-timezone-settings'],
    queryFn: loadTimezones,
  });
};

export const useEmailSettings = () => {
  return useQuery({
    queryKey: ['company-email-settings'],
    queryFn: loadEmailSettings,
  });
};

export const useVerifyEmailSettings = () => {
  return useMutation({ mutationFn: verifyEmailSettings });
};

const DNS_DOMAINS_KEY = ['dns-domains'];
const SMTP_DOMAINS_KEY = ['smtp-domains'];
const EMAIL_DOMAINS_KEY = ['email-domains'];

export const useInvalidateEmailDomains = () => {
  const queryClient = useQueryClient();
  return {
    invalidate: () =>
      Promise.all([
        queryClient.invalidateQueries(DNS_DOMAINS_KEY),
        queryClient.invalidateQueries(SMTP_DOMAINS_KEY),
        queryClient.invalidateQueries(EMAIL_DOMAINS_KEY),
      ]),
  };
};

export const useCreateDnsDomain = () => {
  const { t } = useScopedTranslate(locale);
  const { invalidate } = useInvalidateEmailDomains();
  return useMutation({
    mutationFn: createDnsDomain,
    onSuccess: async () => {
      await invalidate();
      showSuccess(t('domainCreated'));
    },
    onError: (error: AxiosError<{ domain?: string }>) => {
      handleError(error.response?.data?.domain ?? t('domainCreateError'));
    },
  });
};

export const useDeleteDnsDomain = () => {
  const { t } = useScopedTranslate(locale);
  const { invalidate } = useInvalidateEmailDomains();
  return useMutation({
    mutationFn: deleteDnsDomain,
    onSuccess: async () => {
      await invalidate();
      showSuccess(t('domainDeleted'));
    },
    onError: () => {
      handleError(t('domainDeleteError'));
    },
  });
};

export const useVerifyDnsDomain = () => {
  const { invalidate } = useInvalidateEmailDomains();
  return useMutation({
    mutationFn: verifyDnsDomain,
    onSuccess: (data) => data.isVerified && invalidate(),
  });
};

export const useCreateSmtpDomain = () => {
  const { t } = useScopedTranslate(locale);
  const { invalidate } = useInvalidateEmailDomains();
  return useMutation({
    mutationFn: createSmtpDomain,
    onSuccess: async () => {
      await invalidate();
      showSuccess(t('domainCreated'));
    },
    onError: (
      error: AxiosError<{
        smtpHost?: string;
        oauth2AuthorizationCode?: string;
      }>
    ) => {
      const data = error.response?.data;
      handleError(data?.smtpHost ?? data?.oauth2AuthorizationCode ?? t('domainCreateError'));
    },
  });
};

export const useUpdateSmtpDomain = () => {
  const { t } = useScopedTranslate(locale);
  const { invalidate } = useInvalidateEmailDomains();
  return useMutation({
    mutationFn: updateSmtpDomain,
    onSuccess: async () => {
      await invalidate();
      showSuccess(t('domainUpdated'));
    },
    onError: (
      error: AxiosError<{
        smtpHost?: string;
        oauth2AuthorizationCode?: string;
      }>
    ) => {
      const data = error.response?.data;
      handleError(data?.smtpHost ?? data?.oauth2AuthorizationCode ?? t('domainUpdateError'));
    },
  });
};

export const useDeleteSmtpDomain = () => {
  const { t } = useScopedTranslate(locale);
  const { invalidate } = useInvalidateEmailDomains();
  return useMutation({
    mutationFn: deleteSmtpDomain,
    onSuccess: async () => {
      await invalidate();
      showSuccess(t('domainDeleted'));
    },
    onError: () => {
      handleError(t('domainDeleteError'));
    },
  });
};

export const useRefreshSmtpDomainToken = () => {
  const { t } = useScopedTranslate(locale);
  const { invalidate } = useInvalidateEmailDomains();
  return useMutation({
    mutationFn: refreshSmtpDomainToken,
    onSuccess: async () => {
      await invalidate();
      showSuccess(t('tokenRefreshed'));
    },
    onError: () => {
      handleError(t('tokenRefreshError'));
    },
  });
};

export const useEmailDomains = () => {
  return useQuery({
    queryKey: EMAIL_DOMAINS_KEY,
    queryFn: loadEmailDomains,
  });
};

export const useEmailAddressOptions = () => {
  const domainsQuery = useEmailDomains();

  return useMemo(() => {
    if (!domainsQuery.data || domainsQuery.data.length === 0) {
      return {
        options: [
          {
            label: 'Journeys',
            value: 'system',
            helpText: 'noreply@mg.50skills.app',
          },
        ] as SelectOption[],
        defaultId: 'system',
        isLoading: domainsQuery.isLoading,
      };
    }

    const options: SelectOption[] = [];

    let defaultId: string | undefined;

    domainsQuery.data.forEach((domain) => {
      domain.emailAddresses
        .filter((addr) => addr.isVerified && addr.isActive)
        .sort((a, b) => a.fromName.localeCompare(b.fromName))
        .forEach((address) => {
          options.push({
            label: address.fromName,
            helpText: address.fromAddress,
            value: address.shortId,
          });
          if (address.isDefault) {
            defaultId = address.shortId;
          }
        });
    });

    return { options, defaultId, isLoading: domainsQuery.isLoading };
  }, [domainsQuery.data, domainsQuery.isLoading]);
};

export const useCreateEmailAddress = () => {
  const { t } = useScopedTranslate(locale);
  const { invalidate } = useInvalidateEmailDomains();
  return useMutation({
    mutationFn: createEmailAddress,
    onSuccess: async () => {
      await invalidate();
      showSuccess(t('addressCreated'));
    },
    onError: (error: AxiosError<{ fromAddress?: string }>) => {
      handleError(error.response?.data?.fromAddress ?? t('addressCreateError'));
    },
  });
};

export const useUpdateEmailAddress = () => {
  const { t } = useScopedTranslate(locale);
  const { invalidate } = useInvalidateEmailDomains();
  return useMutation({
    mutationFn: updateEmailAddress,
    onSuccess: async () => {
      invalidate();
      showSuccess(t('addressUpdated'));
    },
    onError: () => {
      handleError(t('addressUpdateError'));
    },
  });
};

export const useDeleteEmailAddress = () => {
  const { t } = useScopedTranslate(locale);
  const { invalidate } = useInvalidateEmailDomains();
  return useMutation({
    mutationFn: deleteEmailAddress,
    onSuccess: async () => {
      await invalidate();
      showSuccess(t('addressDeleted'));
    },
    onError: () => {
      handleError(t('addressDeleteError'));
    },
  });
};

export const useSendEmailAddressVerification = () => {
  const { t } = useScopedTranslate(locale);
  return useMutation({
    mutationFn: sendEmailAddressVerification,
    onSuccess: () => {
      showSuccess(t('verificationSent'));
    },
    onError: () => {
      handleError(t('verificationSendError'));
    },
  });
};

export const useVerifyEmailAddress = () => {
  const { t } = useScopedTranslate(locale);
  const { invalidate } = useInvalidateEmailDomains();
  return useMutation({
    mutationFn: verifyEmailAddress,
    onSuccess: async () => {
      await invalidate();
      showSuccess(t('addressVerified'));
    },
    onError: () => {
      handleError(t('verificationError'));
    },
  });
};

export const useSetDefaultEmailAddress = () => {
  const { t } = useScopedTranslate(locale);
  const { invalidate } = useInvalidateEmailDomains();
  return useMutation({
    mutationFn: setDefaultEmailAddress,
    onSuccess: async () => {
      await invalidate();
      showSuccess(t('addressSetDefault'));
    },
    onError: () => {
      handleError(t('addressSetDefaultError'));
    },
  });
};

export const useCheckEmailAddressUsage = () => {
  return useMutation({
    mutationFn: checkEmailAddressUsage,
  });
};

export const useAzureAdSettingsForEmail = (email?: string) => {
  return useQuery({
    queryKey: ['company-azure-ad-settings-for-email'],
    queryFn: () => loadAzureADSettingsForEmail(email),
    enabled: false,
  });
};

export const useAzureAdSettings = () => {
  return useQuery({
    queryKey: ['company-azure-ad-settings'],
    queryFn: loadAzureADSettings,
  });
};

export const useAddAzureAdSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addAzureADSettings,
    onSuccess: async () => {
      await queryClient.invalidateQueries(['company-azure-ad-settings']);
    },
  });
};

export const useRemoveAzureAdSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeAzureADSettings,
    onSuccess: () => {
      queryClient.invalidateQueries(['company-azure-ad-settings']);
    },
  });
};

export const useBillingDetails = () => {
  return useQuery({
    queryKey: ['company-billing-details'],
    queryFn: () => loadBillingDetails(),
  });
};

type UpdateBillingError = AxiosError<{ error: string }>;

export const useUpdateBillingInfo = () => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: editBillingDetails,
    onSuccess: async () => {
      showSuccess(t('billingDetailsUpdated'));
      queryClient.invalidateQueries(['company-billing-details']);
    },
    onError: (error: UpdateBillingError) => {
      handleError(error.response?.data?.error ?? t('billingDetailsNotUpdated'));
    },
  });
};

export const useListInvoices = () => {
  return useQuery({
    queryKey: ['company-list-invoices'],
    queryFn: () => loadInvoices(),
  });
};

export const useGetInvoicePdf = (invoiceId: string) => {
  return useQuery({
    queryKey: ['company-invoice-pdf', invoiceId],
    queryFn: () => loadInvoicePdf(invoiceId),
  });
};

export const useGetChargeBeePaymentMethodPage = () => {
  return useQuery({
    queryKey: ['chargebee-payment-page'],
    queryFn: () => loadChargebeePaymentMethodPage(),
  });
};

export const useGetChargeBeePlanPage = () => {
  const { t } = useScopedTranslate(locale);
  return useMutation({
    mutationFn: loadChargebeePlanPages,
    onError: () => {
      handleError(t('plansPageError'));
    },
  });
};

const locale = {
  en: {
    companyUpdated: 'Company details updated',
    companyNotUpdated: 'Company details not updated',
    emailSettingsUnknownError: 'Unknown error',
    emailSettingsRemoved: 'Email settings removed',
    portalDomainEditError: 'Domain already registered, please use a unique domain',
    portalDomainEditSuccess:
      'Domain saved. Please follow the instructions on the page to set up the DNS.',
    billingDetailsUpdated: 'Billing details updated',
    billingDetailsNotUpdated: 'Billing details not updated, an error occurred',
    plansPageError: 'Error loading checkout page',
    domainCreated: 'Email domain added',
    domainCreateError: 'Failed to add email domain',
    domainUpdated: 'Email domain updated',
    domainUpdateError: 'Failed to update email domain',
    domainDeleted: 'Email domain removed',
    domainDeleteError: 'Failed to remove email domain',
    tokenRefreshed: 'OAuth2 token refreshed',
    tokenRefreshError: 'Failed to refresh OAuth2 token',
    addressCreated: 'Email address added',
    addressCreateError: 'Failed to add email address',
    addressUpdated: 'Email address updated',
    addressUpdateError: 'Failed to update email address',
    addressDeleted: 'Email address removed',
    addressDeleteError: 'Failed to remove email address',
    addressVerified: 'Email address verified',
    addressSetDefault: 'Default email address updated',
    addressSetDefaultError: 'Failed to set default email address',
    verificationSent: 'Verification email sent',
    verificationSendError: 'Failed to send verification email',
    verificationError: 'Invalid verification code',
  },
};
